import { repository } from 'redux-scaffolding-ts';
import { DataStore, Query, QueryResult } from 'stores/dataStore';
import { ValidationResult } from 'fluent-ts-validator';
import * as buildQuery from 'odata-query';
import i18n from 'i18n';
import { container } from 'inversify.config';
import HttpService from 'services/http-service';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';

export interface ChecklistReportChecklistItemDto {
  id: string;
  instructor: string;
  status: string;
  checkListItem: string;
  comment: string;
  checked: boolean;
}

export interface ChecklistReportEventDto {
  eventGuid: string;
  eventId: string;
  eventType: string;
  machineModel: string;
  location: string;
  completedChecklistItems: number;
  totalChecklistItems: number;
  checklistItems: ChecklistReportChecklistItemDto[];
}

export interface ChecklistReportFlatItemDto {
  id: string;
  eventGuid: string;
  eventId: string;
  eventType: string;
  machineModel: string;
  location: string;
  instructor: string;
  status: string;
  checkListItem: string;
  comment: string;
  checked: boolean;
  completedChecklistItems: number;
  totalChecklistItems: number;
  completionRate: number;
}

export type ChecklistReportDto = ChecklistReportEventDto | ChecklistReportFlatItemDto;

export interface ChecklistMetricDto {
  eventType: string;
  completedChecklistItems: number;
  totalChecklistItems: number;
  completionRate: number;
}

@repository('@@CHECKLIST_REPORTS', 'checklistReports.list')
export class ChecklistReportStore extends DataStore<ChecklistReportDto> {
  CHECKLIST_REPORTS_EXPORTED = 'CHECKLIST_REPORTS_EXPORTED';
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = '';
  retrievePath = 'get-checklist-reports-grouped';
  updatePath = '';
  deletePath = '';
  exportPath = 'export-checklist-reports';

  protected validate(_: ChecklistReportDto) {
    return new ValidationResult();
  }

  public async getFromEndpointAsync(query: Query, endpoint: string): Promise<QueryResult<ChecklistReportDto>> {
    const httpService = container.get<HttpService>(HttpService);
    const { path, body } = DataStore.getRequestParts(query);
    const data = body == null ? undefined : { ...body };
    const result = await this.dispatchAsync(
      this.ENTITY_LIST_UPDATE,
      httpService.get<QueryResult<ChecklistReportDto>>(`${this.baseUrl}/${endpoint}?${path}`, data)
    );
    return result.data;
  }

  constructor() {
    super('CHECKLIST_REPORT_LIST', {
      isBusy: false,
      items: [],
      count: 0,
      metrics: [],
      result: undefined,
      discard: () => {}
    });

    this.addReducer(this.CHECKLIST_REPORTS_EXPORTED, this.onExported, 'AsyncAction');
  }

  // Export ignores paging: only $filter, $orderby and custom parameters are sent.
  public async exportToExcel(query: Query) {
    const orderBy = (query.orderBy || []).map(o => `${o.field} ${o.direction === 'Ascending' ? 'asc' : 'desc'}`);
    const odata = buildQuery.default({ filter: query.filter, orderBy: orderBy.length > 0 ? orderBy : undefined }).substr(1);
    const parameters = Object.entries(query.parameters || {})
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

    const queryString = [odata, ...parameters].filter(Boolean).join('&');
    const url = `${this.baseUrl}/${this.exportPath}${queryString ? `?${queryString}` : ''}`;

    const httpService = container.get<HttpService>(HttpService);
    return this.dispatchAsync(this.CHECKLIST_REPORTS_EXPORTED, httpService.download(url, 'ChecklistReport.xlsx'));
  }

  private onExported = () => ({
    onStart: () => ({ ...this.state, isBusy: true }),
    onSuccess: () => {
      ToastComponent({ text: String(i18n.t('File download successfully!')), type: 'success-toast' });
      return { ...this.state, isBusy: false };
    },
    onError: error => {
      const noData = error?.message === 'There is no data';
      ToastComponent({
        text: String(i18n.t(noData ? 'There is no data to export' : 'File download failed')),
        type: noData ? 'warning-toast' : 'error-toast'
      });
      return { ...this.state, isBusy: false };
    }
  });
}
